import { mat2, vec2 } from "@tlaukkan/tsm";
import { BloodCell, ColliderShape, OnBuyOther, Physics, Transform } from "./components";
import { Entity } from "ecsy";
import { PhysicsSystem } from "./systems/physicsSystem";
import { perpendicular, printObjectProperties, rotateAround, vecToStr } from "./utils";
import { PhysicsConstants } from "./constants";

export class B2DFrameLocalMemory {
	private memory = new Array<any>();

	register(ptr: any) {
		this.memory.push(ptr);
	}

	clear() {
		for (const ptr of this.memory) {
			B2D.destroy(ptr);
		}
		this.memory = []
	}
}

export let FrameMemoryArena = new B2DFrameLocalMemory();

/**Convert a vec2 to temporary Box2d allocated b2vec2 for passing to a Box2D function, 
 * which will be deallocated at end of next frame. */
export function passVec2(vec: vec2) {
	const b2v = new B2D.b2Vec2(vec.x, vec.y);
	FrameMemoryArena.register(b2v);
	return b2v;
}

export function passVec2FromComponents(x: number, y: number) {
	const b2v = new B2D.b2Vec2(x, y);
	FrameMemoryArena.register(b2v);
	return b2v;
}

export function passArrayVec2(vec: [number, number]) {
	const b2v = new B2D.b2Vec2(vec[0], vec[1]);
	FrameMemoryArena.register(b2v);
	return b2v;
}

export function claimVec2(vec: B2D.b2Vec2) {
	//FrameMemoryArena.register(vec); //seems like it gets cleaned up ok. Must be passing by reference
	return new vec2([vec.get_x(), vec.get_y()]);
}

export function findClosestInRange(
	position: vec2,
	physics: Physics,
	range: number,
	filter: (entity: Entity) => boolean
): Entity | null {
	let nearest: Entity | null = null;
	let nearestSqrDistance = Infinity;

	const world = physics.body.GetWorld()

	const aabb = new B2D.b2AABB();
	aabb.set_lowerBound(passVec2(new vec2([position.x - range, position.y - range])));
	aabb.set_upperBound(passVec2(new vec2([position.x + range, position.y + range])));

	const aabbCallback = new B2D.JSQueryCallback();
	aabbCallback.ReportFixture = (fixturePtr) => {
		const fixture = B2D.wrapPointer(fixturePtr, B2D.b2Fixture);
		const target = PhysicsSystem.fixtureToEntityMap.get(fixture);
		const targetTransform = target?.getComponent(Transform);
		if (target && targetTransform && filter(target)) {
			const sqrDistance = targetTransform.position.copy().subtract(position).squaredLength()
			if (sqrDistance < nearestSqrDistance) {
				nearestSqrDistance = sqrDistance;
				nearest = target;
			}
		}
		return true; //continue looping
	};

	world.QueryAABB(aabbCallback, aabb);

	B2D.destroy(aabb);
	B2D.destroy(aabbCallback);

	return nearest;
}

export function queryAABB(
	center: vec2,
	halfWidth: number,
	halfHeight: number,
	filter: (entity: Entity) => boolean
) {
	const aabb = new B2D.b2AABB();
	aabb.set_lowerBound(passVec2(new vec2([center.x - halfWidth, center.y - halfHeight]))); //TODO: does this miss corners?
	aabb.set_upperBound(passVec2(new vec2([center.x + halfWidth, center.y + halfHeight])));

	const results = new Array<Entity>();
	const aabbCallback = new B2D.JSQueryCallback();
	aabbCallback.ReportFixture = (fixturePtr) => {
		const fixture = B2D.wrapPointer(fixturePtr, B2D.b2Fixture);
		const target = PhysicsSystem.fixtureToEntityMap.get(fixture);
		const targetPhysics = target?.getComponent(Physics);
		if (target && targetPhysics && filter(target)) {
			results.push(target);
		}
		return true; //continue looping
	};

	PhysicsSystem.world.QueryAABB(aabbCallback, aabb);

	B2D.destroy(aabb);
	B2D.destroy(aabbCallback);

	return results
}


export function raycast(start: vec2, end: vec2, nearestOnly = false) {
	const callback = new B2D.JSRayCastCallback();
	const results = new Array<Entity>();
	callback.ReportFixture = (fixturePtr, point, normal, fraction) => {
		const fixture = B2D.wrapPointer(fixturePtr, B2D.b2Fixture);
		const target = PhysicsSystem.fixtureToEntityMap.get(fixture);
		if (target && target.hasComponent(Physics)) {
			results.push(target);
		}
		if (nearestOnly) {
			return 0;
		}
		return 1;
	}
	PhysicsSystem.world.RayCast(callback, passVec2(start), passVec2(end));
	return results;
}

export function queryRadius(
	center: vec2,
	radius: number,
	filter: (entity: Entity) => boolean
) {
	const halfDiagonal = Math.sqrt(2 * (radius * radius)); //pythagorean theorem
	const check = (entity: Entity) => {
		const transform = entity.getComponent(Transform);
		if (filter(entity) && transform) {
			//The center of the target object is not within the circle, but the bounds could be
			const physics = entity.getComponent(Physics);
			if (physics && physics.shapeType === ColliderShape.Circle) {
				//Test distance from object is within cicle (not in corners)
				const betweenCenters = transform.position.copy().subtract(center);
				const sqrRadiusSum = Math.pow((transform.size.x / 2) + radius, 2);
				const sqrDistance = betweenCenters.squaredLength();
				const centerInCircle = sqrDistance <= sqrRadiusSum;
				if (centerInCircle) { return true; }
			}
			else { //Box shape
				return circleOverlapsBox(center, radius, transform.position, transform.size, transform.rotation);
			}

		}
		return false;
	};

	return queryAABB(center, halfDiagonal, halfDiagonal, check);
}

export function queryBox(
	center: vec2,
	size: vec2,
	rotationRadians: number,
	filter: (entity: Entity) => boolean,
) {
	//Half the diagonal, since a rotated box's corners reach past half its longest side
	const halfExtent = Math.hypot(size.x, size.y) / 2;

	const broadPhase = queryAABB(center, halfExtent, halfExtent, filter);
	if (broadPhase.length === 0) return broadPhase;

	const results = new Array<Entity>();
	// now do "narrow phase"
	for (const other of broadPhase) {
		const otherTransform = other.getComponent(Transform);
		const otherPos = otherTransform.position;
		const otherSize = otherTransform.size;
		const otherRot = otherTransform.rotation;

		const otherIsCircle = other.getComponent(Physics)?.shapeType === ColliderShape.Circle;
		const overlaps = otherIsCircle
			? circleOverlapsBox(otherPos, otherSize.x / 2, center, size, rotationRadians)
			: boxOverlaps(center.x, center.y, size.x, size.y, rotationRadians, otherPos.x, otherPos.y, otherSize.x, otherSize.y, otherRot);
		if (overlaps) {
			results.push(other);
		}
	}
	return results;
}

export function testOverlap(center: vec2, size: vec2, rotationRadians: number, shape: ColliderShape, otherCenter: vec2, otherSize: vec2, otherRotationRadians: number, otherShape: ColliderShape) {
	if (shape === ColliderShape.Circle && otherShape === ColliderShape.Circle) {
		const betweenCenters = center.copy().subtract(otherCenter);
		const sqrDistance = betweenCenters.squaredLength();
		const sqrRadiusSum = Math.pow((size.x / 2) + (otherSize.x / 2), 2);
		return sqrDistance <= sqrRadiusSum;
	}
	else if (shape === ColliderShape.Circle) {
		return circleOverlapsBox(center, size.x / 2, otherCenter, otherSize, otherRotationRadians);
	}
	else if (otherShape === ColliderShape.Circle) {
		return circleOverlapsBox(otherCenter, otherSize.x / 2, center, size, rotationRadians);
	}
	else {
		return boxOverlaps(
			center.x, center.y, size.x, size.y, rotationRadians,
			otherCenter.x, otherCenter.y, otherSize.x, otherSize.y, otherRotationRadians
		);
	}
}

function circleOverlapsBox(circleCenter: vec2, radius: number, boxCenter: vec2, boxSize: vec2, boxRotationRadians: number) {
	//Move the circle into the box's frame, undoing the rotation computeCorners applies
	const dx = circleCenter.x - boxCenter.x;
	const dy = circleCenter.y - boxCenter.y;
	const cos = Math.cos(boxRotationRadians);
	const sin = Math.sin(boxRotationRadians);
	const localX = dx * cos + dy * sin;
	const localY = -dx * sin + dy * cos;

	//Closest point on the box to the circle center
	const halfW = boxSize.x / 2;
	const halfH = boxSize.y / 2;
	const closestX = Math.max(-halfW, Math.min(halfW, localX));
	const closestY = Math.max(-halfH, Math.min(halfH, localY));

	const sqrDistance = Math.pow(localX - closestX, 2) + Math.pow(localY - closestY, 2);
	return sqrDistance <= radius * radius;
}

function boxOverlaps(
	centerX1: number, centerY1: number, w1: number, h1: number, rotationRadians1: number,
	centerX2: number, centerY2: number, w2: number, h2: number, rotationRadians2: number
): boolean {
	// Compute the four corners of each box
	const box1 = computeCorners(centerX1, centerY1, w1, h1, rotationRadians1);
	const box2 = computeCorners(centerX2, centerY2, w2, h2, rotationRadians2);

	//TODO: I think since these are rectangles we only need 2 not 4 axes each.
	const axes1 = computeRectangleAxes(box1);
	const axes2 = computeRectangleAxes(box2);

	for (const axis of axes1) {
		const projection1 = projectAxis(box1, axis);
		const projection2 = projectAxis(box2, axis);
		if (projection1.max < projection2.min || projection2.max < projection1.min) {
			return false;
		}
	}
	for (const axis of axes2) {
		const projection1 = projectAxis(box1, axis);
		const projection2 = projectAxis(box2, axis);
		if (projection1.max < projection2.min || projection2.max < projection1.min) {
			return false;
		}
	}

	return true; // No separating axis found, boxes overlap

	function computeRectangleAxes(corners: vec2[]) {
		const axes = [];
		{
			const diff = vec2.difference(corners[0], corners[1]);
			const axis = perpendicular(diff, diff).normalize();
			axes.push(axis);
		}
		{
			const diff = vec2.difference(corners[1], corners[2]);
			const perp = perpendicular(diff, diff);
			const axis = perp.normalize();
			axes.push(axis);
		}
		return axes;
	}

	function projectAxis(corners: vec2[], axis: vec2) {
		let min = Infinity;
		let max = -Infinity;
		for (const vertex of corners) {
			const projection = vec2.dot(axis, vertex);
			if (projection < min) {
				min = projection;
			}
			if (projection > max) {
				max = projection;
			}
		}
		return { min, max };
	}
}

export function computeCorners(centerX: number, centerY: number, w: number, h: number, rotationRadians: number) {
	const cos = Math.cos(rotationRadians);
	const sin = Math.sin(rotationRadians);

	const x1 = centerX + (-w / 2) * cos - (-h / 2) * sin;
	const y1 = centerY + (-w / 2) * sin + (-h / 2) * cos;

	const x2 = centerX + (w / 2) * cos - (-h / 2) * sin;
	const y2 = centerY + (w / 2) * sin + (-h / 2) * cos;

	const x3 = centerX + (w / 2) * cos - (h / 2) * sin;
	const y3 = centerY + (w / 2) * sin + (h / 2) * cos;

	const x4 = centerX + (-w / 2) * cos - (h / 2) * sin;
	const y4 = centerY + (-w / 2) * sin + (h / 2) * cos;

	return [new vec2([x1, y1]), new vec2([x2, y2]), new vec2([x3, y3]), new vec2([x4, y4])];
	//return [{ x: x1, y: y1 }, { x: x2, y: y2 }, { x: x3, y: y3 }, { x: x4, y: y4 }];
}


/**Apply an impulse, taking into acocount compound bodies */
export function applyImpulse(entity: Entity, impulse: vec2, pointOfApplication?: vec2) {
	const physics = entity.getComponent(Physics);
	const transform = entity.getComponent(Transform);
	const body = physics.body;
	if (!pointOfApplication) pointOfApplication = transform.position;

	body.ApplyLinearImpulse(passVec2(impulse), passVec2(pointOfApplication), true);
}

/**Apply a force, taking into account compound bodies */
export function applyForce(entity: Entity, impulse: vec2) {
	const physics = entity.getComponent(Physics);
	const transform = entity.getComponent(Transform);
	const body = physics.body;

	body.ApplyForce(passVec2(impulse), passVec2(transform.position), true);
}

/**Sets the velocity. If entity shares a compound body it does something equivalent for just this entity's contribution to velocity */
// export function setLinearVelocity(entity: Entity, velocity: vec2) {
// 	const physics = entity.getMutableComponent(Physics);
// 	const body = physics.body;
// 	const prevContribution = physics.lastVelocityContribution;
// 	const currentV = claimVec2(body.GetLinearVelocity());
// 	const numFixtures = PhysicsSystem.bodyToEntitiesMap.get(body)!.size;
// 	const otherFixtureContributions = currentV.scale(numFixtures).subtract(prevContribution);

// 	body.SetLinearVelocity(passVec2(otherFixtureContributions.add(velocity)));
// 	//physics.lastVelocityContribution = velocity;
// }

export const bodiesToJointsMap = new Map<B2D.b2Body, B2D.b2Joint[]>();

function makeDistanceJoint(me: B2D.b2Body, other: B2D.b2Body, distance: number) {
	const world = me.GetWorld()
	const jointDef = new B2D.b2DistanceJointDef();
	jointDef.set_bodyA(me);
	jointDef.set_bodyB(other);
	jointDef.set_localAnchorA(passVec2(new vec2([0, 0])));
	jointDef.set_localAnchorB(passVec2(new vec2([0, 0])));
	jointDef.set_length(distance)
	return B2D.castObject(world.CreateJoint(jointDef), B2D.b2DistanceJoint);
}

export function makeWeldJoint(me: B2D.b2Body, other: B2D.b2Body) {
	const world = me.GetWorld()
	const jointDef = new B2D.b2WeldJointDef();
	//Anchor at the midpoint rather than me's position, so the lever arm is split evenly between both
	//bodies instead of landing entirely on the linked one (which was causing wobble under rotation).
	const midpoint = vec2.mix(claimVec2(me.GetPosition()), claimVec2(other.GetPosition()), 0.5);
	jointDef.Initialize(me, other, passVec2(midpoint));
	return B2D.castObject(world.CreateJoint(jointDef), B2D.b2WeldJoint);
}

export function clearAllJoints(body: B2D.b2Body) {
	const jointsToDelete = new Array<B2D.b2Joint>();
	let edge = body.GetJointList();
	//@ts-ignore
	while (!B2D.compare(edge, B2D.NULL)) {
		//@ts-ignore
		jointsToDelete.push(edge.get_joint());
		//@ts-ignore
		edge = edge.get_next();
	}

	for (const j of jointsToDelete) {
		PhysicsSystem.world.DestroyJoint(j);
	}
}

export function severWeldJoint(joint: B2D.b2Joint) {
	printObjectProperties(joint);
	PhysicsSystem.world.DestroyJoint(joint);
}

export function calculateTargetLead(target: Entity,
	fromPosition: vec2,
	targetPhysics: Physics = target.getComponent(Physics),
	targetTransform: Transform = target.getComponent(Transform)
) {
	const targetPos = targetTransform.position.copy();
	const targetVel = claimVec2(targetPhysics.body.GetLinearVelocity());
	//const targetSpeed = targetVel.length() / PhysicsConstants.StepRate;
	const targetSpeed = targetVel.length() * PhysicsConstants.StepRate;

	const distance = vec2.distance(fromPosition, targetPos);
	const leadScaler = targetSpeed > 0 ? Math.min(distance, targetSpeed) / targetSpeed : 1;
	return targetPos.add(targetVel.normalize().scale(leadScaler));

	//Might this work better? I forgot source of original
	// const leadScaler = targetSpeed > 0 ? Math.min(distance, targetSpeed) / targetSpeed : 1;
	// return targetPos.add(targetVel.scale(PhysicsConstants.StepRate).scale(leadScaler));
}